<template>
  <div>
    <label v-if="label" :class="labelClasses">{{ label }}</label>
    <div class="relative">
      <FontAwesome
        v-if="icon"
        :icon="icon"
        class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-(--nviya-text-tertiary)"
      />
      <component
        :is="element"
        :value="modelValue"
        :type="element === 'input' ? type : undefined"
        :placeholder="placeholder"
        :disabled="disabled"
        :rows="element === 'textarea' ? rows : undefined"
        :min="min"
        :max="max"
        :step="step"
        :class="inputClasses"
        v-bind="$attrs"
        @input="$emit('update:modelValue', $event.target.value)"
      >
        <slot />
      </component>
    </div>
    <p v-if="error" class="mt-1.5 text-xs text-(--nviya-critical-text)">{{ error }}</p>
    <p v-else-if="hint" class="mt-1.5 text-xs text-(--nviya-text-tertiary)">{{ hint }}</p>
  </div>
</template>

<script setup>
import { computed } from 'vue'

defineOptions({ inheritAttrs: false })

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ''
  },
  element: {
    type: String,
    default: 'input',
    validator: v => ['input', 'select', 'textarea'].includes(v)
  },
  type: {
    type: String,
    default: 'text'
  },
  placeholder: {
    type: String,
    default: ''
  },
  label: {
    type: String,
    default: null
  },
  disabled: Boolean,
  size: {
    type: String,
    default: 'md',
    validator: v => ['sm', 'md', 'lg'].includes(v)
  },
  rows: {
    type: [String, Number],
    default: 3
  },
  min: {
    type: [String, Number],
    default: undefined
  },
  max: {
    type: [String, Number],
    default: undefined
  },
  step: {
    type: [String, Number],
    default: undefined
  },
  /** Leading icon, FontAwesome array form. Padding is added for you. */
  icon: { type: Array, default: null },
  hint: { type: String, default: null },
  error: { type: String, default: null }
})

defineEmits(['update:modelValue'])

const sizeClasses = {
  sm: 'px-2.5 py-1.5 text-xs',
  md: 'px-3 py-2 text-sm',
  lg: 'px-4 py-2.5 text-sm'
}

// `n-field` carries the ground, the hairline, the placeholder ink and the focus
// treatment (see main.css) — in dark mode it sinks the input to --nviya-canvas
// so a field is visibly BELOW the card it sits on rather than level with it.
// Keeping that in CSS means an input hand-rolled elsewhere can opt into the
// identical behaviour with one class.
const inputClasses = computed(() => [
  'w-full rounded-(--nviya-radius-md) border n-field',
  'transition-[border-color,box-shadow,background-color] duration-150',
  sizeClasses[props.size],
  props.icon && (props.size === 'sm' ? 'pl-8' : 'pl-9'),
  props.element === 'select' && 'cursor-pointer appearance-none pr-8',
  props.element === 'textarea' && 'resize-y min-h-20',
  props.error && 'n-field-error',
  props.disabled && 'opacity-55 cursor-not-allowed'
])

const labelClasses = 'block text-xs font-semibold mb-1.5 text-(--nviya-text-secondary)'
</script>
